import { Box, Button, Chip, Collapse, Dialog, DialogContent, Divider, Paper, Typography } from "@mui/material"
import { BoxFC, BoxFR } from "components/BoxCustom"
import PaperComponent from "components/PaperComponent"
import { DialogFooter, DialogHeader } from "components/dialog/DialogHeader"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import React, { useContext, useEffect, useMemo, useState } from "react"
import ComboBox from "components/ComboBox"
import CheckboxFormControl from "components/CheckboxFormControl"
import { AddCircleRounded, CheckBoxOutlineBlankRounded, CheckBoxRounded, EditRounded, RemoveCircleRounded, SwapHorizRounded } from "resource/muiImport"
import { blue, green, grey, orange, red } from "@mui/material/colors"
import { buildPettyCashLogEvents, snapshotFields } from "./pettyCashLog"

const DiffLine = ({ diff }) => (
  <Typography variant="body2" component="div" sx={{ pl: 4 }}>
    <Box component="span" color="text.secondary">{diff.label}: </Box>
    <Box component="span" sx={{ color: grey[600], textDecoration: diff.from ? "line-through" : "none" }}>{diff.from || "–"}</Box>
    {" → "}
    <Box component="span" fontWeight="bold">{diff.to || "–"}</Box>
  </Typography>
)

const FlagLine = ({ flag }) => (
  <BoxFR sx={{ gap: 1 }}>
    {flag.to ? <CheckBoxRounded fontSize="small" sx={{ color: green[700] }} />
      : <CheckBoxOutlineBlankRounded fontSize="small" sx={{ color: grey[600] }} />}
    <Typography variant="body2">{flag.to ? "ติ๊ก" : "เอาติ๊กออก"} <strong>{flag.label}</strong></Typography>
  </BoxFR>
)

const CHANGE_STYLE = {
  add: { icon: AddCircleRounded, color: green[700], verb: "เพิ่ม" },
  delete: { icon: RemoveCircleRounded, color: red[700], verb: "ลบ" },
  moveIn: { icon: SwapHorizRounded, color: blue[700], verb: "ย้ายเข้า" },
  moveOut: { icon: SwapHorizRounded, color: orange[800], verb: "ย้ายออก" },
  edit: { icon: EditRounded, color: grey[700], verb: "แก้ไข" },
  noop: { icon: EditRounded, color: grey[400], verb: "บันทึกโดยไม่มีการเปลี่ยนแปลง" },
}

const ChangeLine = ({ change }) => {
  const [showSnapshot, setShowSnapshot] = useState(false)
  const style = CHANGE_STYLE[change.type]
  const Icon = style.icon
  const showSummary = ["add", "delete", "moveIn", "moveOut"].includes(change.type)
  return (
    <BoxFC sx={{ gap: 0.25 }}>
      {change.type !== "edit" || change.diffs.length > 0 ?
        <BoxFR sx={{ gap: 1, alignItems: "flex-start" }}>
          <Icon fontSize="small" sx={{ color: style.color, mt: 0.25 }} />
          <Typography variant="body2" color={change.type === "noop" ? "text.secondary" : "text.primary"}>
            <Box component="span" sx={{ color: style.color, fontWeight: "bold" }}>{style.verb}</Box>
            {" "}{change.label}
            {showSummary && change.summary ? <Box component="span" color="text.secondary">{"  "}{change.summary}</Box> : null}
            {change.type === "moveIn" ? <Box component="span" color="text.secondary">{"  "}จาก {change.source}</Box> : null}
            {change.type === "moveOut" ? <Box component="span" color="text.secondary">{"  "}ไปยัง {change.target}</Box> : null}
          </Typography>
          {change.type === "delete" ?
            <Button size="small" sx={{ py: 0, minWidth: 0 }} onClick={() => setShowSnapshot(o => !o)}>
              {showSnapshot ? "ซ่อน" : "ดูข้อมูลก่อนลบ"}
            </Button> : null}
        </BoxFR>
        : null}
      {change.type !== "add" && change.type !== "delete" ? change.diffs.map(d => <DiffLine key={d.field} diff={d} />) : null}
      {change.flags.map(f => <FlagLine key={f.field} flag={f} />)}
      {change.type === "delete" ?
        <Collapse in={showSnapshot}>
          <Box sx={{ ml: 4, mt: 0.5, p: 1, bgcolor: red[50], borderRadius: 1, display: "grid", gridTemplateColumns: "max-content 1fr", columnGap: 2 }}>
            {snapshotFields(change.kind, change.row).map(f => (
              <React.Fragment key={f.label}>
                <Typography variant="body2" color="text.secondary">{f.label}</Typography>
                <Typography variant="body2">{f.value}</Typography>
              </React.Fragment>
            ))}
          </Box>
        </Collapse> : null}
    </BoxFC>
  )
}

const EventCard = ({ event, showNoop }) => {
  const changes = showNoop ? event.changes : event.changes.filter(c => c.type !== "noop")
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderLeft: 4, borderLeftColor: event.tag === "delete" ? red[400] : event.tag === "add" ? green[400] : blue[200] }}>
      <BoxFR sx={{ gap: 1, mb: 0.5 }}>
        <Typography variant="body2" fontWeight="bold">{event.time.format("DD/MM/YYYY HH:mm:ss")}</Typography>
        <Typography variant="body2">{event.accNm}</Typography>
        {event.logRmks.map(rmk => <Typography key={rmk} variant="caption" color="text.secondary">({rmk})</Typography>)}
        <Box flex={1} />
        {event.tag === "add" ? <Chip size="small" label="สร้าง" sx={{ bgcolor: green[100] }} /> : null}
        {event.tag === "delete" ? <Chip size="small" label="ลบ" sx={{ bgcolor: red[100] }} /> : null}
      </BoxFR>
      <BoxFC sx={{ gap: 0.5 }}>
        {changes.map((c, i) => <ChangeLine key={`${c.key}-${c.logId}-${i}`} change={c} />)}
      </BoxFC>
    </Paper>
  )
}

const PettyCashLogDialog = ({ pcTrnsId, dialogOpen, setDialogOpen, updLnkSiteAbbr }) => {
  const { ax } = useContext(GlobalStateContext)
  const [events, setEvents] = useState([])
  const [accId, setAccId] = useState(null)
  const [showNoop, setShowNoop] = useState(false)

  const accOptions = useMemo(() => {
    const map = new Map()
    for (const e of events) map.set(e.accId, e.accNm)
    return [...map.entries()].map(([id, label]) => ({ id, label }))
  }, [events])

  const shownEvents = useMemo(() => events.filter(e => (accId === null || e.accId === accId)
    && (showNoop || e.changes.some(c => c.type !== "noop"))), [events, accId, showNoop])

  useEffect(() => {
    if (dialogOpen && pcTrnsId) {
      ax.post("/petty-cash/getPettyCashLog", { PCTrnsId: pcTrnsId, UpdLnkSiteAbbr: updLnkSiteAbbr }).then(value => {
        if (value.data) {
          setEvents(buildPettyCashLogEvents(value.data))
        }
      })
    } else {
      setEvents([])
      setAccId(null)
      setShowNoop(false)
    }
  }, [dialogOpen, ax, pcTrnsId, updLnkSiteAbbr])

  return (
    <Dialog
      open={dialogOpen}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      fullWidth maxWidth='md'
      onClose={(e, reason) => {
        if (reason === "backdropClick") return
        setDialogOpen(false)
      }}
    >
      <DialogHeader handleDialogClose={() => setDialogOpen(false)}
        title={`ประวัติการเปลี่ยนแปลง Transaction #${pcTrnsId}`}
        onDeleteClick={null}
      />
      <DialogContent>
        <BoxFC pt={2} sx={{ gap: 1 }}>
          <BoxFR sx={{ gap: 2 }}>
            <Typography variant="body2" color="text.secondary">{shownEvents.length} รายการ</Typography>
            <Box flex={1} />
            <CheckboxFormControl label="แสดงการบันทึกที่ไม่มีการเปลี่ยนแปลง" checked={showNoop}
              onChange={(e) => setShowNoop(e.target.checked)} />
            <ComboBox sx={{ width: 250 }} options={accOptions} label="ผู้แก้ไข"
              selectedId={accId} setSelectedId={(id) => setAccId(id)} />
          </BoxFR>
          <Divider />
          <BoxFC sx={{ gap: 1, height: 600, overflowY: "auto", pr: 1 }}>
            {shownEvents.map(e => <EventCard key={e.id} event={e} showNoop={showNoop} />)}
            {shownEvents.length === 0 ?
              <Typography variant="body2" color="text.secondary" textAlign="center" pt={4}>ไม่มีประวัติการเปลี่ยนแปลง</Typography>
              : null}
          </BoxFC>
        </BoxFC>
      </DialogContent>
      <DialogFooter okText="" cancelText="ปิด"
        handleDialogClose={() => setDialogOpen(false)}
        handleDialogOk={null} />
    </Dialog>
  )
}

export default React.memo(PettyCashLogDialog)
